import { useState, useRef, useEffect } from "react";
import { Gauge, Repeat, Music2, Save, Timer, Bell, Minus, Plus, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { fmtTime } from "@/lib/music";

const SPEEDS = [25, 50, 75, 100];
const PARTS = ["Soprano", "Alto", "Tenor", "Bass", "Piano", "Guitar", "Bass Guitar", "Drums", "Other"];

export default function PracticePanel({ song, sections, bpm, player, transpose, setTranspose, currentTime }) {
  const [part, setPart] = useState("Soprano");
  const [speed, setSpeed] = useState(100);
  const [loopIdx, setLoopIdx] = useState(-1);
  const [metronome, setMetronome] = useState(false);
  const [reps, setReps] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  const audioCtxRef = useRef(null);
  const metroTimer = useRef(null);
  const startRef = useRef(Date.now());
  const prevTime = useRef(0);

  // speed -> playback rate
  useEffect(() => { player.setRate(speed / 100); }, [speed]);

  // loop region
  useEffect(() => {
    if (loopIdx >= 0 && sections[loopIdx]) {
      const s = sections[loopIdx];
      player.setLoop({ start: s.start, end: s.end });
      player.seek(s.start);
    } else {
      player.setLoop(null);
    }
  }, [loopIdx]);

  // count reps on loop wrap
  useEffect(() => {
    if (loopIdx >= 0 && prevTime.current > currentTime + 1) setReps((r) => r + 1);
    prevTime.current = currentTime;
  }, [currentTime, loopIdx]);

  // practice duration timer
  useEffect(() => {
    const t = setInterval(() => {
      if (player.playing) setElapsed(Math.floor((Date.now() - startRef.current) / 1000));
    }, 1000);
    return () => clearInterval(t);
  }, [player.playing]);
  useEffect(() => { startRef.current = Date.now() - elapsed * 1000; }, []);

  // metronome
  useEffect(() => {
    if (metronome && player.playing) startMetro();
    else stopMetro();
    return stopMetro;
  }, [metronome, player.playing, bpm, speed]);

  const startMetro = () => {
    stopMetro();
    if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    const ctx = audioCtxRef.current;
    const effBpm = (bpm?.value || 100) * (speed / 100);
    const interval = 60000 / effBpm;
    let beat = 0;
    const click = () => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = beat % 4 === 0 ? 1500 : 900;
      gain.gain.setValueAtTime(0.001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + 0.001);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.05);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(); osc.stop(ctx.currentTime + 0.06);
      beat++;
    };
    click();
    metroTimer.current = setInterval(click, interval);
  };
  const stopMetro = () => { if (metroTimer.current) { clearInterval(metroTimer.current); metroTimer.current = null; } };

  const countInAndPlay = async () => {
    if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    const ctx = audioCtxRef.current;
    const effBpm = (bpm?.value || 100);
    const interval = 60000 / effBpm;
    for (let i = 0; i < 4; i++) {
      setTimeout(() => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.frequency.value = 1200;
        gain.gain.setValueAtTime(0.001, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + 0.001);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.05);
        osc.connect(gain); gain.connect(ctx.destination);
        osc.start(); osc.stop(ctx.currentTime + 0.06);
      }, i * interval);
    }
    setTimeout(() => player.play(), 4 * interval);
    toast("Count-in… 1 – 2 – 3 – 4");
  };

  const save = async () => {
    try {
      await api.post("/practice/sessions", {
        song_id: song.id,
        song_title: song.title,
        part,
        section: loopIdx >= 0 ? sections[loopIdx].name : "Full song",
        speed,
        transpose,
        repetitions: reps,
        duration_seconds: elapsed,
      });
      toast.success("Practice session saved");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-4">
        <div className="flex items-center gap-2 font-display font-bold text-white">
          <GraduationCap size={18} className="text-cyan-400" /> Learn This Song
        </div>
        <p className="mt-1 text-xs text-slate-400">Pick your part, slow it down, loop a section and practice.</p>
      </div>

      {/* Part */}
      <div className="rounded-xl border border-[#222A38] bg-[#131822] p-4">
        <Label icon={Music2}>Your part / instrument</Label>
        <div className="flex flex-wrap gap-2">
          {PARTS.map((p) => (
            <button
              key={p}
              data-testid={`practice-part-${p}`}
              onClick={() => setPart(p)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                part === p ? "bg-cyan-500 text-[#0B0E14]" : "bg-[#1A202C] text-slate-400 hover:text-white"
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* Speed */}
      <div className="rounded-xl border border-[#222A38] bg-[#131822] p-4">
        <Label icon={Gauge}>Playback speed — <span className="font-mono-sw text-cyan-400">{speed}%</span></Label>
        <div className="mb-3 flex gap-2">
          {SPEEDS.map((s) => (
            <button
              key={s}
              data-testid={`speed-${s}`}
              onClick={() => setSpeed(s)}
              className={`flex-1 rounded-lg py-2 text-sm font-semibold transition-colors ${
                speed === s ? "bg-cyan-500 text-[#0B0E14]" : "bg-[#1A202C] text-slate-400 hover:text-white"
              }`}
            >
              {s}%
            </button>
          ))}
        </div>
        <div className="sw-slider-track">
          <input data-testid="speed-slider" type="range" min="25" max="150" step="5" value={speed}
            onChange={(e) => setSpeed(parseInt(e.target.value))} className="w-full" />
        </div>
      </div>

      {/* Transpose */}
      <div className="rounded-xl border border-[#222A38] bg-[#131822] p-4">
        <Label icon={Music2}>Transpose</Label>
        <div className="flex items-center justify-center gap-4">
          <button data-testid="practice-transpose-down" onClick={() => setTranspose(Math.max(-12, transpose - 1))} className="rounded-lg bg-[#1A202C] p-2 text-white"><Minus size={16} /></button>
          <span className="w-16 text-center font-display text-2xl font-extrabold text-cyan-400">{transpose > 0 ? `+${transpose}` : transpose}</span>
          <button data-testid="practice-transpose-up" onClick={() => setTranspose(Math.min(12, transpose + 1))} className="rounded-lg bg-[#1A202C] p-2 text-white"><Plus size={16} /></button>
        </div>
      </div>

      {/* Loop section */}
      {sections.length > 0 && (
        <div className="rounded-xl border border-[#222A38] bg-[#131822] p-4">
          <Label icon={Repeat}>Loop section</Label>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            <button
              data-testid="loop-none"
              onClick={() => setLoopIdx(-1)}
              className={`rounded-lg py-2 text-xs font-semibold ${loopIdx === -1 ? "bg-cyan-500 text-[#0B0E14]" : "bg-[#1A202C] text-slate-400"}`}
            >
              Off
            </button>
            {sections.map((s, i) => (
              <button
                key={`${s.name}-${s.start}`}
                data-testid={`loop-section-${i}`}
                onClick={() => setLoopIdx(i)}
                className={`rounded-lg py-2 text-xs font-semibold ${loopIdx === i ? "bg-cyan-500 text-[#0B0E14]" : "bg-[#1A202C] text-slate-400"}`}
              >
                {s.name} <span className="opacity-60">{fmtTime(s.start)}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Tools */}
      <div className="grid grid-cols-2 gap-3">
        <button
          data-testid="metronome-toggle"
          onClick={() => setMetronome((m) => !m)}
          className={`flex items-center justify-center gap-2 rounded-xl border p-3 text-sm font-semibold transition-colors ${
            metronome ? "border-amber-500/40 bg-amber-500/10 text-amber-300" : "border-[#222A38] bg-[#131822] text-slate-300"
          }`}
        >
          <Bell size={16} /> Metronome {metronome ? "On" : "Off"}
        </button>
        <button data-testid="count-in-btn" onClick={countInAndPlay} className="flex items-center justify-center gap-2 rounded-xl border border-[#222A38] bg-[#131822] p-3 text-sm font-semibold text-slate-300 hover:text-white">
          <Timer size={16} /> Count-in & Play
        </button>
      </div>

      {/* Session summary */}
      <div className="rounded-xl border border-[#222A38] bg-[#131822] p-4">
        <div className="mb-3 grid grid-cols-3 gap-3 text-center">
          <div><div className="font-display text-lg font-bold text-white">{fmtTime(elapsed)}</div><div className="font-mono-sw text-[10px] uppercase text-slate-500">Time</div></div>
          <div><div data-testid="rep-count" className="font-display text-lg font-bold text-white">{reps}</div><div className="font-mono-sw text-[10px] uppercase text-slate-500">Loops</div></div>
          <div><div className="font-display text-lg font-bold text-white">{speed}%</div><div className="font-mono-sw text-[10px] uppercase text-slate-500">Speed</div></div>
        </div>
        <button data-testid="save-session-btn" onClick={save} className="flex w-full items-center justify-center gap-2 rounded-xl bg-cyan-500 py-3 font-semibold text-[#0B0E14] hover:bg-cyan-400">
          <Save size={16} /> Save Practice Session
        </button>
      </div>
    </div>
  );
}

function Label({ icon: Icon, children }) {
  return (
    <div className="mb-3 flex items-center gap-2 font-mono-sw text-[11px] uppercase tracking-wider text-slate-400">
      <Icon size={13} className="text-cyan-400" /> {children}
    </div>
  );
}
